import { Check } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import styles from './form.module.css';

interface CheckboxListProps<T extends string | number> {
  legend: string;
  hint?: ReactNode;
  error?: string;
  options: Array<{ value: T; label: ReactNode }>;
  /** In the order ticked. */
  value: T[];
  onChange: (value: T[]) => void;
  /** Short options as wrapping chips (like a choice group) instead of full-width rows. */
  chips?: boolean;
}

/** Several independent yes/no choices, each a full-width touch target (or a chip). */
export function CheckboxList<T extends string | number>({ legend, hint, error, options, value, onChange, chips }: CheckboxListProps<T>) {
  const name = useId();
  const toggle = (v: T, on: boolean) => onChange(on ? [...value, v] : value.filter((x) => x !== v));
  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.label}>{legend}</legend>
      {hint && <p className={styles.hint}>{hint}</p>}
      <div className={chips ? styles.choices : styles.checkList}>
        {options.map((o) => {
          const checked = value.includes(o.value);
          if (chips) {
            return (
              <label key={String(o.value)} className={checked ? `${styles.choice} ${styles.choiceSelected}` : styles.choice}>
                <input
                  type="checkbox"
                  name={name}
                  className="visually-hidden"
                  checked={checked}
                  onChange={(e) => toggle(o.value, e.target.checked)}
                />
                {checked && <Check aria-hidden="true" className={styles.choiceCheck} />}
                <span>{o.label}</span>
              </label>
            );
          }
          return (
            <label key={String(o.value)} className={checked ? `${styles.checkRow} ${styles.checkRowOn}` : styles.checkRow}>
              <input
                type="checkbox"
                name={name}
                className="visually-hidden"
                checked={checked}
                onChange={(e) => toggle(o.value, e.target.checked)}
              />
              <span className={styles.checkBox} aria-hidden="true">
                {checked && <Check />}
              </span>
              <span>{o.label}</span>
            </label>
          );
        })}
      </div>
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </fieldset>
  );
}
